/**
 * Which columns the grid draws. A wide table's last columns are usually the
 * ones nobody is reading this minute — a JSON blob, an audit timestamp — and
 * hiding them gives the rest their width back.
 *
 * Only the view: the page query, the filter, the sort and the CSV export still
 * see every column. The last visible column cannot be hidden; a grid of no
 * columns is not a state anyone means to reach.
 */
import { useCallback, useRef, useState } from 'react';
import { AppDatabaseColumn } from '@back4app2/sdk';
import { CheckIcon, ColumnsIcon } from '../../components/ds/icons';
import { Popover, PopoverItem, SectionLabel } from './controls';
import { useDismiss } from './useDismiss';

const ColumnsMenu = ({ columns, hidden, onChange }: {
  columns: AppDatabaseColumn[],
  hidden: string[],
  onChange: (hidden: string[]) => void,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);
  useDismiss(rootRef, isOpen, close);

  const visibleCount = columns.filter(column => !hidden.includes(column.name)).length;

  return (
    <div className="relative flex-none" ref={rootRef}>
      {/* Icon only, and the count moved into the title: this sits in a row
          whose real work is the filter, and "Columns 4/6" was wider than the
          field it was crowding. */}
      <button
        type="button"
        onClick={() => setIsOpen(open => !open)}
        aria-expanded={isOpen}
        aria-label="Columns"
        title={`Columns — ${visibleCount} of ${columns.length} visible`}
        className={[
          'b4-focus flex-none flex items-center justify-center w-11 h-11 md:w-[30px] md:h-[30px] rounded-[6px] border transition-colors duration-[120ms]',
          isOpen
            ? 'border-navy-300 text-white'
            : 'border-navy-400 text-navy-200 hover:text-white hover:border-navy-300',
        ].join(' ')}
      >
        <ColumnsIcon size={16} />
      </button>

      {isOpen ? (
        <Popover align="right" width={240}>
          <SectionLabel>Visible</SectionLabel>
          <div className="max-h-[320px] overflow-auto b4-thin">
            {columns.map(column => {
              const isOn = !hidden.includes(column.name);
              const isLast = isOn && visibleCount === 1;

              return (
                <PopoverItem
                  key={column.name}
                  role="menuitemcheckbox"
                  aria-checked={isOn}
                  isDisabled={isLast}
                  icon={<CheckIcon size={16} className={isOn ? 'text-state-ok-soft' : 'opacity-0'} />}
                  onClick={() => onChange(isOn
                    ? [...hidden, column.name]
                    : hidden.filter(name => name !== column.name))}
                >
                  <span className="font-courrier">{column.name}</span>
                </PopoverItem>
              );
            })}
          </div>
        </Popover>
      ) : null}
    </div>
  );
};

export default ColumnsMenu;
